import React from "react";
import type {
  ActionFunctionArgs,
  LoaderFunctionArgs,
  MetaFunction,
} from "@remix-run/node";
import { json, redirect } from "@remix-run/node";
import {
  Form,
  Link,
  useActionData,
  useNavigation,
  useSearchParams,
  useNavigate,
} from "@remix-run/react";
import { getAuthUser, signIn } from "~/services/auth.server";
import { GoogleSignInButton } from "~/components/auth/GoogleSignInButton";

export const meta: MetaFunction = () => {
  return [
    { title: "로그인 - 슬로우다이브" },
    { name: "description", content: "계정에 로그인하세요" },
  ];
};

export async function loader({ request }: LoaderFunctionArgs) {
  const user = await getAuthUser(request);
  if (user) {
    // 이미 로그인된 사용자는 redirect 파라미터가 있으면 해당 페이지로, 없으면 홈으로
    const url = new URL(request.url);
    const redirectTo = url.searchParams.get("redirect") || "/";
    return redirect(redirectTo);
  }
  return json({});
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get("email") as string;
  const password = formData.get("password") as string;

  if (!email || !password) {
    return json(
      { error: "이메일과 비밀번호를 입력해주세요." },
      { status: 400 }
    );
  }

  try {
    const { headers } = await signIn(request, email, password);
    return json({ success: true }, { headers });
  } catch (error) {
    let errorMessage = "로그인에 실패했습니다.";
    if (error instanceof Error) {
      const message = error.message.toLowerCase();
      if (message.includes("이메일 인증이 필요합니다")) {
        errorMessage =
          "이메일 인증이 필요합니다. 이메일을 확인하여 계정을 활성화해주세요.";
      } else if (message.includes("이메일 또는 비밀번호가 올바르지 않습니다")) {
        errorMessage = "이메일 또는 비밀번호가 올바르지 않습니다.";
      } else if (message.includes("너무 많은 로그인 시도")) {
        errorMessage =
          "너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.";
      } else {
        errorMessage = error.message;
      }
    }
    return json({ error: errorMessage }, { status: 400 });
  }
}

export default function Login() {
  const actionData = useActionData<typeof action>();
  const navigation = useNavigation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isSubmitting = navigation.state === "submitting";
  const message = searchParams.get("message");

  // Handle successful login
  React.useEffect(() => {
    if (actionData && "success" in actionData && actionData.success) {
      // 로그인 성공 시 redirect 파라미터가 있으면 해당 페이지로, 없으면 홈으로
      const redirectTo = searchParams.get("redirect") || "/";
      window.location.href = redirectTo;
    }
  }, [actionData, searchParams]);

  return (
    <div className="min-h-[calc(100vh-8.5rem-394px)] flex items-center justify-center bg-gray-50 dark:bg-gray-950 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
            계정에 로그인
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-300">
            또는{" "}
            <Link
              to={`/signup${
                searchParams.get("redirect")
                  ? `?redirect=${encodeURIComponent(
                      searchParams.get("redirect")!
                    )}`
                  : ""
              }`}
              className="font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              새 계정 만들기
            </Link>
          </p>
        </div>

        {/* 회원가입 성공 메시지 */}
        {message && (
          <div className="bg-green-50 border border-green-200 dark:bg-green-900 dark:border-green-700 rounded-md p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-green-400 dark:text-green-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800 dark:text-green-400">
                  회원가입 완료
                </h3>
                <div className="mt-2 text-sm text-green-700 dark:text-green-300">
                  <p>{decodeURIComponent(message)}</p>
                  {message.includes("이메일을 확인") && (
                    <p className="mt-2">
                      ⚠️ 이메일을 확인하지 못하셨나요? 스팸 폴더도 확인해보세요.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <Form className="mt-8 space-y-6" method="post">
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="email" className="sr-only">
                이메일 주소
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 focus:z-10 sm:text-sm"
                placeholder="이메일 주소"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                비밀번호
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 dark:text-gray-100 dark:border-gray-600 dark:placeholder-gray-400 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400 focus:z-10 sm:text-sm"
                placeholder="비밀번호"
              />
            </div>
          </div>

          {actionData && "error" in actionData && actionData.error && (
            <div className="text-red-600 text-sm text-center dark:text-red-400">
              {actionData.error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-400 dark:hover:bg-blue-500 dark:text-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? "로그인 중..." : "로그인"}
            </button>
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-gray-50 dark:bg-gray-950 text-gray-500 dark:text-gray-400">
                  또는
                </span>
              </div>
            </div>

            <div className="mt-6">
              <GoogleSignInButton
                text="Google로 로그인"
                redirectTo={searchParams.get("redirect") || undefined}
              />
            </div>
          </div>
        </Form>
      </div>
    </div>
  );
}
